if (!customElements.get('calm-room-quiz')) {
  customElements.define('calm-room-quiz', class CalmRoomQuiz extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      try {
        this.quizData = JSON.parse(this.querySelector('[data-quiz-data]').textContent);
      } catch {
        return;
      }
      this.products = this.quizData.products.filter((product) => product.url && product.variants.some((variant) => variant.available));
      if (!this.products.length) return;
      this.ready = true;
      this.answers = {};
      this.croAttempt = {};
      this.step = 1;
      this.adding = false;
      this.querySelector('[data-quiz-app]').hidden = false;
      this.querySelector('[data-quiz-fallback]').hidden = true;
      this.addEventListener('click', (event) => this.handleClick(event));
      this.querySelector('[data-quiz-cart-form]').addEventListener('submit', (event) => this.addToCart(event));
      this.showStep(1, false);
    }

    handleClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || this.adding) return;
      if (button.hasAttribute('data-quiz-answer')) {
        const panel = button.closest('[data-quiz-question]');
        this.answers[panel.dataset.quizQuestion] = button.dataset.quizAnswer;
        panel.querySelectorAll('[data-quiz-answer]').forEach((choice) => choice.setAttribute('aria-pressed', String(choice === button)));
        this.querySelector('[data-quiz-next]').disabled = false;
        if (event.isTrusted) window.CalmCRO?.once(this.croAttempt, 'started', 'quiz_started');
      } else if (button.hasAttribute('data-quiz-next')) {
        if (!this.answers[this.currentQuestion()]) return;
        const completedStep = this.step;
        if (this.step === 6) this.renderResult();
        this.showStep(this.step + 1);
        if (event.isTrusted) {
          window.CalmCRO?.once(this.croAttempt, 'step_' + completedStep, 'quiz_step_completed', { step: completedStep });
          if (this.step === 7 && this.resultProduct) window.CalmCRO?.once(this.croAttempt, 'result', 'quiz_result_viewed');
        }
      } else if (button.hasAttribute('data-quiz-back')) {
        this.showStep(Math.max(1, this.step - 1));
      } else if (button.hasAttribute('data-quiz-edit')) {
        this.showStep(1);
      } else if (button.hasAttribute('data-quiz-restart')) {
        this.answers = {};
        this.croAttempt = {};
        this.querySelectorAll('[data-quiz-answer]').forEach((choice) => choice.setAttribute('aria-pressed', 'false'));
        this.showStep(1);
      } else if (button.hasAttribute('data-quiz-variant')) {
        const previous = this.selectedVariant?.id;
        this.answers.size = button.dataset.quizVariant;
        this.renderBuying();
        if (event.isTrusted && previous !== this.selectedVariant?.id) {
          window.CalmCRO?.emit('size_selected', { source: 'quiz', size: this.sizeOf(this.selectedVariant) });
        }
      }
    }

    currentQuestion() {
      return this.querySelector('[data-quiz-panel="' + this.step + '"]')?.dataset.quizQuestion;
    }

    showStep(number, moveFocus = true) {
      this.step = number;
      this.querySelectorAll('[data-quiz-panel]').forEach((panel) => { panel.hidden = Number(panel.dataset.quizPanel) !== number; });
      const result = number === 7;
      this.querySelector('[data-quiz-navigation]').hidden = result;
      this.querySelector('[data-quiz-progress-row]').hidden = result;
      this.querySelector('[data-quiz-track]').hidden = result;
      this.querySelector('[data-quiz-intro]').hidden = number > 1;
      if (!result) {
        this.querySelector('[data-quiz-progress-text]').textContent = this.quizData.labels.progress.replace('[current]', number);
        this.querySelector('[data-quiz-progress-fill]').style.width = (number / 6 * 100) + '%';
        this.querySelector('[data-quiz-back]').hidden = number === 1;
        const next = this.querySelector('[data-quiz-next]');
        next.disabled = !this.answers[this.currentQuestion()];
        next.textContent = number === 6 ? this.quizData.labels.finish : this.quizData.labels.next;
      }
      const panel = this.querySelector('[data-quiz-panel="' + number + '"]');
      const heading = panel.querySelector('h2');
      this.querySelector('[data-quiz-status]').textContent = (!result ? this.querySelector('[data-quiz-progress-text]').textContent + '. ' : '') + heading.textContent;
      if (moveFocus) {
        heading.focus({ preventScroll: true });
        heading.scrollIntoView({ block: 'start', behavior: 'auto' });
      }
    }

    preferenceWeights() {
      return {
        goal: {
          light: { klarhed: 4, sindsro: 2, fokus: 1 },
          warm: { haab: 4, tryghed: 2 },
          depth: { tryghed: 4, haab: 2, sindsro: 1 },
          simple: { fokus: 4, klarhed: 2, sindsro: 2 }
        },
        palette: {
          blue: { klarhed: 3, sindsro: 3, fokus: 2 },
          warm: { haab: 3, tryghed: 1 },
          green: { tryghed: 3, haab: 1 },
          neutral: { fokus: 3, klarhed: 2, sindsro: 1 }
        },
        composition: {
          open: { klarhed: 3, sindsro: 3, fokus: 1 },
          immersive: { tryghed: 3, haab: 3 },
          defined: { fokus: 3, haab: 1 }
        }
      };
    }

    rankProducts() {
      const weights = this.preferenceWeights();
      return this.products.map((product, index) => {
        let score = product.key === this.answers.nature ? 9 : 0;
        Object.keys(weights).forEach((question) => { score += weights[question][this.answers[question]]?.[product.key] || 0; });
        return { ...product, score, index };
      }).sort((first, second) => second.score - first.score || Number(second.key === this.answers.nature) - Number(first.key === this.answers.nature) || first.index - second.index);
    }

    sizeOf(variant) {
      const match = variant.title.match(/(\d+)\s*[x×]\s*(\d+)/i);
      return match ? match[1] + 'x' + match[2] : variant.title;
    }

    variantFor(product, preferAvailable = false) {
      const wanted = this.answers.size === 'undecided' ? '50x70' : this.answers.size;
      const exact = product.variants.find((variant) => this.sizeOf(variant) === wanted);
      if (exact && (!preferAvailable || exact.available)) return exact;
      return product.variants.find((variant) => variant.available) || product.variants[0];
    }

    renderResult() {
      const ranked = this.rankProducts();
      this.resultProduct = ranked[0];
      this.alternativeProduct = ranked[1];
      const product = this.resultProduct;
      if (!product) return;
      const image = this.querySelector('[data-quiz-result-image]');
      image.hidden = !product.image;
      if (product.image) { image.src = product.image; image.alt = product.name; }
      this.querySelector('[data-quiz-result-name]').textContent = product.name;
      this.querySelector('[data-quiz-result-copy]').textContent = product.copy;
      const weights = this.preferenceWeights();
      const contributors = ['nature', 'goal', 'palette', 'composition'].map((question) => ({
        question,
        score: question === 'nature' ? (product.key === this.answers.nature ? 9 : 0) : (weights[question][this.answers[question]]?.[product.key] || 0)
      })).filter((item) => item.score > 0).sort((first, second) => second.score - first.score);
      const reasons = contributors.slice(0, 2).map(({ question }) => {
        const answer = this.querySelector('[data-quiz-question="' + question + '"] [data-quiz-answer="' + this.answers[question] + '"] strong');
        return this.quizData.labels[question + '_prefix'] + ' ' + answer.textContent;
      });
      reasons.push(product.reason);
      const list = this.querySelector('[data-quiz-reasons]');
      list.replaceChildren();
      reasons.forEach((reason) => { const item = document.createElement('li'); item.textContent = reason; list.append(item); });
      this.querySelector('[data-quiz-room-tip]').textContent = this.quizData.room_tips[this.answers.room];
      this.querySelector('[data-quiz-error]').textContent = '';
      this.renderBuying();
    }

    renderBuying() {
      const product = this.resultProduct;
      const variant = this.variantFor(product);
      this.selectedVariant = variant;
      const choices = this.querySelector('[data-quiz-result-sizes]');
      const focusedSize = choices.contains(document.activeElement) ? document.activeElement.dataset.quizVariant : null;
      choices.replaceChildren();
      product.variants.forEach((item) => {
        const size = this.sizeOf(item);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ca-quiz__size';
        button.dataset.quizVariant = size;
        button.textContent = size.replace('x', ' × ') + ' cm';
        button.disabled = !item.available;
        button.setAttribute('aria-pressed', String(item.id === variant.id));
        if (!item.available) button.setAttribute('aria-label', button.textContent + '. ' + this.quizData.labels.unavailable);
        choices.append(button);
        if (focusedSize === size) button.focus({ preventScroll: true });
      });
      this.querySelector('[data-quiz-variant-id]').value = variant.id;
      this.querySelector('[data-quiz-result-price]').textContent = variant.price;
      this.querySelector('[data-quiz-result-link]').href = variant.url || product.url;
      const add = this.querySelector('[data-quiz-add]');
      add.disabled = !variant.available;
      add.textContent = variant.available ? this.quizData.labels.add : this.quizData.labels.unavailable;
      const notice = this.querySelector('[data-quiz-result-notice]');
      notice.textContent = !variant.available ? this.quizData.labels.unavailable : this.answers.size === 'undecided' ? this.quizData.labels.undecided : '';
      notice.hidden = !notice.textContent;
      this.renderAlternative();
    }

    renderAlternative() {
      const alternative = this.alternativeProduct;
      this.querySelector('[data-quiz-alternative]').hidden = !alternative;
      if (!alternative) return;
      const variant = this.variantFor(alternative, true);
      const image = this.querySelector('[data-quiz-alternative-image]');
      image.hidden = !alternative.image;
      if (alternative.image) { image.src = alternative.image; image.alt = alternative.name; }
      this.querySelector('[data-quiz-alternative-name]').textContent = alternative.name;
      this.querySelector('[data-quiz-alternative-copy]').textContent = alternative.copy;
      const link = this.querySelector('[data-quiz-alternative-link]');
      link.href = variant.url || alternative.url;
      link.textContent = this.quizData.labels.alternative.replace('[name]', alternative.name).replace('[price]', variant.price).replace('[size]', this.sizeOf(variant).replace('x', ' × ') + ' cm');
    }

    async addToCart(event) {
      const drawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      if (!drawer?.renderContents) return;
      event.preventDefault();
      if (this.adding || !this.selectedVariant?.available) return;
      this.adding = true;
      const form = event.currentTarget;
      const button = this.querySelector('[data-quiz-add]');
      const error = this.querySelector('[data-quiz-error]');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = this.quizData.labels.adding;
      error.textContent = '';
      drawer.setActiveElement?.(button);
      let added = false;
      let committed = false;
      let stage = 'transport';
      const croRequest = {};
      try {
        const data = new FormData(form);
        data.append('sections', drawer.getSectionsToRender().map((section) => section.id).join(','));
        data.append('sections_url', window.location.pathname);
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' }, body: data });
        added = response.ok;
        const state = await response.json();
        stage = 'api';
        if (!response.ok || state.status) throw new Error(state.description || this.quizData.labels.error);
        committed = true;
        stage = 'render';
        window.CalmCRO?.emit('quiz_add_success');
        drawer.classList.remove('is-empty');
        if (state.sections) drawer.renderContents(state);
        else {
          window.CalmCRO?.once(croRequest, 'error', 'cart_error', { source: 'quiz', stage, committed });
          window.location.assign(window.Shopify.routes.root + 'cart');
        }
      } catch (failure) {
        window.CalmCRO?.once(croRequest, 'error', 'cart_error', { source: 'quiz', stage, committed });
        if (added) window.location.assign(window.Shopify.routes.root + 'cart');
        else error.textContent = failure.message || this.quizData.labels.error;
      } finally {
        this.adding = false;
        button.disabled = !this.selectedVariant.available;
        button.removeAttribute('aria-busy');
        button.textContent = this.quizData.labels.add;
      }
    }
  });
}
